import type { CalendarDate } from '@ui/components/calendar/picker'
import { createSharedComposable } from '@vueuse/core'
import { addDays, endOfDay, startOfDay } from 'date-fns'
import { isEqual } from 'es-toolkit'

interface Filters {
  address: number[]
  service: number[]
  date: CalendarDate
}

function _useCalendarFilters() {
  const { mainServices } = storeToRefs(useAccountStore())

  const filters = reactive<Filters>({
    address: [],
    service: [],
    date: new Date(),
  })

  watch(
    () => filters.address,
    () => {
      filters.service = []
    },
  )

  const servicesOptions = computed(() => {
    if (!filters.address.length) {
      return mainServices.value
    }
    return mainServices.value.filter(s => {
      const addressId = s.addresses?.[0]?.id
      return addressId !== undefined && filters.address.includes(addressId)
    })
  })

  const selectedServices = computed(() => {
    if (!filters.service.length) {
      return servicesOptions.value
    }
    return servicesOptions.value.filter(s => filters.service.includes(s.id))
  })

  const isPeriod = computed(() => selectedServices.value.length < 2)
  const timezone = computed(() => selectedServices.value?.[0]?.addresses?.[0]?.timezone || '')

  function getToday() {
    return startOfDay(useTimeZone(new Date(), timezone.value))
  }

  function getDefaultPeriod(start = getToday()) {
    return { start, end: addDays(start, 7) }
  }

  function setDate(date: CalendarDate) {
    if (!isEqual(filters.date, date)) {
      filters.date = date
    }
  }

  function resetDate() {
    setDate(isPeriod.value ? getDefaultPeriod() : getToday())
  }

  resetDate()

  function selectColumnFilter(filter?: number | Date) {
    if (filter instanceof Date) {
      setDate(isPeriod.value ? getDefaultPeriod(filter) : filter)
      filters.service = []
    } else if (filter !== undefined && servicesOptions.value.length) {
      filters.service = [filter]
    }
  }

  function toPeriod() {
    return filters.date instanceof Date ? getDefaultPeriod(filters.date) : filters.date
  }

  function fromPeriod() {
    return filters.date instanceof Date ? filters.date : filters.date.start
  }

  watch(
    isPeriod,
    v => {
      setDate(v ? toPeriod() : fromPeriod())
    },
    { immediate: true },
  )

  function getPeriod() {
    if (filters.date instanceof Date) {
      return {
        start: filters.date,
        end: endOfDay(filters.date),
      }
    }
    return {
      start: filters.date.start,
      end: endOfDay(filters.date.end),
    }
  }

  return {
    filters,
    isPeriod,
    getPeriod,
    resetDate,
    selectedServices,
    servicesOptions,
    selectColumnFilter,
    timezone,
  }
}

export const useCalendarFilters = createSharedComposable(_useCalendarFilters)
